import { ClassLayout } from '@features/Class/models/ClassLayout';

class LocalStore {
  private accessTokenKey = import.meta.env.VITE_ACCESS_TOKEN_SECRET;
  private refreshTokenKey = import.meta.env.VITE_REFRESH_TOKEN_SECRET;
  private userIdKey = import.meta.env.VITE_USER_ID_SECRET;
  private languageKey = import.meta.env.VITE_LANGUAGE_SECRET || 'cgt_language';
  private sharePwKey = import.meta.env.VITE_SHARE_PW_SECRET;
  private classLayoutKey = import.meta.env.VITE_CLASS_LAYOUT_SECRET;
  private studentAccessTokenKey = 'cgt_student_access_token';
  private studentRefreshTokenKey = 'cgt_student_refresh_token';
  private studentAccountIdKey = 'cgt_student_account_id';
  private trainerAccessTokenKey = 'cgt_trainer_access_token';
  private trainerRefreshTokenKey = 'cgt_trainer_refresh_token';
  private trainerAccountIdKey = 'cgt_trainer_account_id';

  setClassLayout(layout: ClassLayout) {
    localStorage.setItem(this.classLayoutKey, layout);
  }

  getClassLayout() {
    const storedClassLayout = localStorage.getItem(this.classLayoutKey);

    switch (storedClassLayout) {
      case ClassLayout.Table:
        return ClassLayout.Table;
      case ClassLayout.Calendar:
        return ClassLayout.Calendar;
      default:
        return ClassLayout.Table;
    }
  }

  setSharePw(pw: string) {
    localStorage.setItem(this.sharePwKey, pw);
  }

  getSharePw() {
    return localStorage.getItem(this.sharePwKey);
  }

  clearSharePw() {
    localStorage.removeItem(this.sharePwKey);
  }

  getLanguage() {
    try {
      const language = localStorage.getItem(this.languageKey);
      if (language) return language;

      if (this.languageKey !== 'cgt_language') return language;

      const legacyLanguage = localStorage.getItem('undefined');
      if (!legacyLanguage) return language;

      localStorage.setItem(this.languageKey, legacyLanguage);
      localStorage.removeItem('undefined');
      return legacyLanguage;
    } catch {
      return null;
    }
  }

  setLanguage(lang: string) {
    try {
      localStorage.setItem(this.languageKey, lang);
    } catch {
      // The active language still changes when browser storage is unavailable.
    }
  }

  getUserId() {
    const id = localStorage.getItem(this.userIdKey);
    return id ? Number(id) : undefined;
  }

  setUserId(id: number | string) {
    localStorage.setItem(this.userIdKey, String(id));
  }

  deleteUserId() {
    localStorage.removeItem(this.userIdKey);
  }

  getTokens() {
    return {
      accessToken: this.getAccessToken(),
      refreshToken: this.getRefreshToken(),
    };
  }

  setTokens(tokens: { accessToken: string; refreshToken: string }) {
    this.setAccessToken(tokens.accessToken);
    this.setRefreshToken(tokens.refreshToken);
  }

  clearTokens() {
    this.removeAccessToken();
    this.removeRefreshToken();
  }

  private getAccessToken(): string | null {
    return localStorage.getItem(this.accessTokenKey);
  }
  private setAccessToken(refreshToken: string) {
    localStorage.setItem(this.accessTokenKey, refreshToken);
  }

  private removeAccessToken() {
    localStorage.removeItem(this.accessTokenKey);
  }

  private getRefreshToken(): string | null {
    return localStorage.getItem(this.refreshTokenKey);
  }
  private setRefreshToken(refreshToken: string) {
    localStorage.setItem(this.refreshTokenKey, refreshToken);
  }

  private removeRefreshToken() {
    localStorage.removeItem(this.refreshTokenKey);
  }

  getStudentTokens() {
    return {
      accessToken: localStorage.getItem(this.studentAccessTokenKey),
      refreshToken: localStorage.getItem(this.studentRefreshTokenKey),
    };
  }

  setStudentTokens(tokens: { accessToken: string; refreshToken: string }) {
    localStorage.setItem(this.studentAccessTokenKey, tokens.accessToken);
    localStorage.setItem(this.studentRefreshTokenKey, tokens.refreshToken);
  }

  clearStudentTokens() {
    localStorage.removeItem(this.studentAccessTokenKey);
    localStorage.removeItem(this.studentRefreshTokenKey);
  }

  getStudentAccountId() {
    return localStorage.getItem(this.studentAccountIdKey) ?? undefined;
  }

  setStudentAccountId(id: string) {
    localStorage.setItem(this.studentAccountIdKey, id);
  }

  deleteStudentAccountId() {
    localStorage.removeItem(this.studentAccountIdKey);
  }

  getTrainerTokens() {
    return {
      accessToken: localStorage.getItem(this.trainerAccessTokenKey),
      refreshToken: localStorage.getItem(this.trainerRefreshTokenKey),
    };
  }

  setTrainerTokens(tokens: { accessToken: string; refreshToken: string }) {
    localStorage.setItem(this.trainerAccessTokenKey, tokens.accessToken);
    localStorage.setItem(this.trainerRefreshTokenKey, tokens.refreshToken);
  }

  clearTrainerTokens() {
    localStorage.removeItem(this.trainerAccessTokenKey);
    localStorage.removeItem(this.trainerRefreshTokenKey);
  }

  getTrainerAccountId() {
    return localStorage.getItem(this.trainerAccountIdKey) ?? undefined;
  }

  setTrainerAccountId(id: string) {
    localStorage.setItem(this.trainerAccountIdKey, id);
  }

  deleteTrainerAccountId() {
    localStorage.removeItem(this.trainerAccountIdKey);
  }
}

export default new LocalStore();
